import { type MouseEvent } from 'react';

import { colors } from '../../colors';
import { ComparisonIcon, LayoutIcon, GoalIcon, XCircleIcon, TagIcon } from '../icons';
import { Box } from '../Layout';
import { Text } from '../Text';

import * as css from './PropertyControl.css';

const availableIcons = {
    layout: { component: LayoutIcon, size: 15 },
    comparison: { component: ComparisonIcon, size: 14 },
    goal: { component: GoalIcon, size: 14, color: 'currentColor' },
    tag: { component: TagIcon, size: 14, color: 'currentColor' },
};

interface PropertyControlValue {
    label: string;
    icon?: 'layout' | 'comparison' | 'goal' | 'tag';
}

export interface PropertyControlProps {
    /**
     * List of values rendered inside the control.
     * Multiple values will be displayed inline.
     */
    values: PropertyControlValue[];

    /**
     * Click handler for the entire control.
     * When provided, the component behaves like a button.
     */
    onClick?: (event: MouseEvent) => void;

    /**
     * Optional clear handler.
     * When provided, a clear (X) button is rendered.
     */
    onClear?: () => void;

    /**
     * Visual variant of the control.
     */
    variant?: 'default' | 'lighter' | 'darker';

    /**
     * When true, applies disabled styling and suppresses interaction.
     */
    disabled?: boolean;
}

function Value({ label, icon }: PropertyControlValue) {
    return (
        <div className={css.value}>
            <PropertyIcon icon={icon} />
            <Text variant="h3">{label}</Text>
        </div>
    );
}

function PropertyIcon({ icon }: Pick<PropertyControlValue, 'icon'>) {
    if (!icon) {
        return;
    }
    const { component: Icon, ...rest } = availableIcons[icon];

    return (
        <span className={css.icon}>
            <Icon {...rest} />
        </span>
    );
}

export function PropertyControl({ values, onClick, onClear, disabled = false, variant }: PropertyControlProps) {
    const isClickable = typeof onClick === 'function';

    const handleClear = (event: MouseEvent) => {
        event.stopPropagation();
        onClear?.();
    };

    return (
        <div
            className={css.propertyTrigger({ isDisabled: disabled, variant })}
            role={isClickable ? 'button' : undefined}
            onClick={(event) => onClick?.(event)}
        >
            <Box display="flex">
                {values.map((value) => (
                    <Value key={value.label} label={value.label} icon={value.icon} />
                ))}
            </Box>
            {onClear && (
                <button className={css.clearButton} onClick={handleClear} type="button">
                    <XCircleIcon width={20} height={20} weight="fill" color={colors.blue[500]} />
                </button>
            )}
        </div>
    );
}
